/**
 * GS-05 "Confirm schedule": for each date of a multi-day event, which reps are working. The grid's choices become the
 * full staffing set that set_event_staffing (migrations 220/409, amended by 414-417 and 500-505) receives.
 *
 * Mirrors the writer's rules so the operator gets the sentence before the round trip (the server still decides):
 *   - the Lead works every day (the grid keeps the Lead on all days; the Lead is changed on Add rep, which needs edit
 *     rights on the event, migration 417; the grid never changes who is Lead, so 417 does not apply to its saves);
 *   - a rep on every day is saved as all_days (also works days added later), which needs an all-days Lead
 *     (STAFFING_ALL_DAYS_NEEDS_ALL_DAYS_LEAD);
 *   - a rep must work at least one day (STAFFING_SELECTED_WITHOUT_DAYS) -- to take someone off, remove them;
 *   - every staffed day needs the Lead or a named closer on it (STAFFING_DAY_WITHOUT_CLOSER);
 *   - existing day closers are kept while they still work that day.
 * Refusals only the server can judge (a rep with a started, unclosed day cannot lose that day, migration 504; version
 * conflicts) come back from the writer and are shown with describeDateModelError's sentence.
 */
import type {
  StaffingAssignment,
  EventStaffing,
} from "../services/eventDateModel.service";

export interface GridDay {
  id: string; // event_days.id
  date: string; // YYYY-MM-DD
}

/** position id -> the event_day ids that rep works (the Lead's entry is ignored: always every day). */
export type GridSelection = Record<string, string[]>;

/** The grid's starting state from the current staffing: an all-days rep has every day ticked. */
export function selectionFromStaffing(
  assignments: StaffingAssignment[],
  days: GridDay[],
): GridSelection {
  const all = days.map((d) => d.id);
  const out: GridSelection = {};
  for (const a of assignments) {
    out[a.position_id] =
      a.role === "lead" || a.day_scope === "all_days"
        ? [...all]
        : all.filter((id) => a.days.includes(id));
  }
  return out;
}

export type StaffingPlan =
  | {
      ok: true;
      staffing: Pick<EventStaffing, "assignments" | "day_closers">;
    }
  | { ok: false; error: string };

/**
 * The set_event_staffing payload for the grid's selection, or the sentence that explains why it cannot be saved.
 * `nameOf` turns a position id into the rep's name for the sentence; `labelOf` a day id into its date label.
 */
export function planStaffingFromGrid(
  current: StaffingAssignment[],
  selection: GridSelection,
  days: GridDay[],
  dayClosers: Record<string, string>,
  nameOf: (positionId: string) => string,
  labelOf: (dayId: string) => string,
): StaffingPlan {
  const order = days.map((d) => d.id);
  const lead = current.find((a) => a.role === "lead") ?? null;
  const assignments: StaffingAssignment[] = [];
  for (const a of current) {
    if (a.role === "lead") {
      assignments.push({
        position_id: a.position_id,
        role: "lead",
        day_scope: "all_days",
        days: [],
      });
      continue;
    }
    const chosen = new Set(selection[a.position_id] ?? []);
    const picked = order.filter((id) => chosen.has(id));
    if (picked.length === 0) {
      return {
        ok: false,
        error: `Choose at least one day for ${nameOf(a.position_id)}, or remove them from the list.`,
      };
    }
    if (picked.length === order.length) {
      assignments.push({
        position_id: a.position_id,
        role: "assigned",
        day_scope: "all_days",
        days: [],
      });
    } else {
      assignments.push({
        position_id: a.position_id,
        role: "assigned",
        day_scope: "selected",
        days: picked,
      });
    }
  }
  if (
    !lead &&
    assignments.some((a) => a.role !== "lead" && a.day_scope === "all_days")
  ) {
    return {
      ok: false,
      error:
        "Add a Lead rep first. A rep who works every day needs a Lead who also works every day.",
    };
  }
  const worksOn = (pos: string, day: string) =>
    assignments.some(
      (a) =>
        a.position_id === pos &&
        (a.day_scope === "all_days" || a.days.includes(day)),
    );
  const day_closers = Object.fromEntries(
    Object.entries(dayClosers).filter(([day, pos]) => worksOn(pos, day)),
  );
  if (!lead) {
    for (const day of order) {
      const staffed = assignments.some(
        (a) => a.day_scope === "all_days" || a.days.includes(day),
      );
      if (staffed && !day_closers[day]) {
        return {
          ok: false,
          error: `Nobody can close ${labelOf(day)}: put the Lead on that day (add a Lead rep), or name a closer.`,
        };
      }
    }
  }
  return { ok: true, staffing: { assignments, day_closers } };
}

/** Has the operator changed anything since the grid was loaded? */
export function selectionChanged(
  initial: GridSelection,
  next: GridSelection,
): boolean {
  const keys = new Set([...Object.keys(initial), ...Object.keys(next)]);
  for (const k of keys) {
    const a = [...(initial[k] ?? [])].sort().join(",");
    const b = [...(next[k] ?? [])].sort().join(",");
    if (a !== b) {
      return true;
    }
  }
  return false;
}
